import { lazy } from "react";
import { Navigate } from "react-router-dom";

import { MODE_ENV } from "@/services/constants";

import { isAdminOnlyMode, LIVE_TRANSACTIONS_PATTERN, PATHS } from "../path";
import PublicRoute from "../PublicRoute";

const LoginForm = lazy(() => import("@/modules/LoginForm/LoginForm"));
const ForgotPassword = lazy(
  () => import("@/modules/ForgotPassword/ForgotPassword")
);
const PasswordResetLinkSend = lazy(
  () => import("@/modules/ForgotPassword/PasswordResetLinkSend")
);
const ChangePasswordPage = lazy(
  () => import("@/modules/ForgotPassword/ChangePasswordPage")
);
const PhoneVerificationForm = lazy(
  () => import("@/modules/PhoneNumberVerification/PhoneVerificationForm")
);
const VerifySuccessfully = lazy(
  () => import("@/modules/PhoneNumberVerification/VerifySuccessfully")
);
const VerifyExpired = lazy(() => import("@/modules/VerifyExpired"));

const RestaurantLiveTransaction = lazy(
  () =>
    import("@/modules/RestaurantLiveTransactions/RestaurantLiveTransactions")
);
const WelcomeToSpicePage = lazy(
  () => import("@/modules/WelcomeToSpice/WelcomeToSpicePage")
);
const SuccessfulActivation = lazy(
  () => import("@/modules/WelcomeToSpice/SuccessfulActivation")
);
const InvitationExpired = lazy(
  () => import("@/modules/WelcomeToSpice/ExpiredInvitation")
);
const LinkExpired = lazy(() => import("@/modules/ForgotPassword/LinkExpired"));
const FoodicsInstallPage = lazy(
  () => import("@/modules/Foodics/FoodicsInstallPage")
);

const withPublic = (element: React.ReactNode) => (
  <PublicRoute>{element}</PublicRoute>
);

const verifyPhoneRoutes = [
  {
    index: true,
    element: withPublic(<PhoneVerificationForm />),
  },
  {
    path: PATHS.VERIFY_PHONE.SUCCESS,
    element: withPublic(<VerifySuccessfully />),
  },
  {
    path: PATHS.VERIFY_PHONE.ERROR,
    element: withPublic(<VerifyExpired />),
  },
];

const MODE = import.meta.env.VITE_MODE_APP;
const isAdmin = MODE === MODE_ENV.ADMIN;

export const publicRoutes = [
  // In admin-only builds, `/` is owned by protected admin routes.
  ...(!isAdminOnlyMode
    ? [
        {
          path: PATHS.HOME,
          element: (
            <Navigate
              to={isAdmin ? PATHS.ADMIN.LOGIN : PATHS.RESTAURANT.LOGIN}
              replace
            />
          ),
        },
      ]
    : []),
  {
    path: LIVE_TRANSACTIONS_PATTERN,
    element: <RestaurantLiveTransaction />,
  },

  // Welcome to SPICE restaurant routes
  {
    path: PATHS.RESTAURANT.WELCOME_TO_SPICE(":token"),
    element: <WelcomeToSpicePage />,
  },
  {
    path: PATHS.RESTAURANT.SUCCESSFUL_ACTIVATION,
    element: <SuccessfulActivation />,
  },
  {
    path: PATHS.RESTAURANT.INVITATION_EXPIRED,
    element: <InvitationExpired />,
  },

  // Welcome to SPICE admin routes
  {
    path: PATHS.ADMIN.WELCOME_TO_SPICE(":token"),
    element: <WelcomeToSpicePage />,
  },
  {
    path: PATHS.ADMIN.SUCCESSFUL_ACTIVATION,
    element: <SuccessfulActivation />,
  },
  {
    path: PATHS.ADMIN.INVITATION_EXPIRED,
    element: <InvitationExpired />,
  },

  // Auth admin routes
  {
    path: PATHS.ADMIN.LOGIN,
    element: withPublic(<LoginForm />),
  },
  {
    path: PATHS.ADMIN.FORGOT_PASSWORD,
    element: withPublic(<ForgotPassword />),
  },
  {
    path: PATHS.ADMIN.PASSWORD_LINK_SEND,
    element: withPublic(<PasswordResetLinkSend />),
  },
  {
    path: PATHS.ADMIN.RESET_PASSWORD(":token"),
    element: withPublic(<ChangePasswordPage />),
  },
  {
    path: PATHS.ADMIN.RESET_PASSWORD_ERROR,
    element: withPublic(<LinkExpired />),
  },

  // Auth restaurant routes
  {
    path: PATHS.RESTAURANT.LOGIN,
    element: withPublic(<LoginForm />),
  },
  {
    path: PATHS.RESTAURANT.FORGOT_PASSWORD,
    element: withPublic(<ForgotPassword />),
  },

  // Foodics partner OAuth
  {
    path: PATHS.FOODICS.INSTALL,
    element: <FoodicsInstallPage />,
  },
  {
    path: PATHS.FOODICS.OAUTH_CALLBACK,
    element: <FoodicsInstallPage />,
  },

  {
    path: PATHS.VERIFY_PHONE.ROOT,
    children: verifyPhoneRoutes,
  },
];
